import React, { useEffect, useState, useRef } from 'react';
import { motion } from 'framer-motion';
import { Icon } from '@iconify/react';
import { toast } from 'sonner';
import { useSelector } from 'react-redux';
import * as XLSX from 'xlsx';
import { createCampaign } from '../../utils/api/campaignAPI';
import { getPhoneNumberByOrgId } from '../../utils/api/phoneNumberAPI';

function AddCampaignModal({ show, onClose, onSuccess }) {
  const user = useSelector((state) => state.user?.user);
  const orgId = useSelector((state) => state?.orgData?.orgData?.organization?.id);
  const listOfAssistants = useSelector((state) => state.assistants.value);

  const fileInputRef = useRef(null);

  const [formData, setFormData] = useState({
    name: '',
    assistantId: '',
    phoneNumberId: '',
    customers: [],
    scheduleType: 'now', // 'now' or 'later'
    scheduleDateTime: '',
  });

  const [phoneNumbers, setPhoneNumbers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loadingPhoneNumbers, setLoadingPhoneNumbers] = useState(false);
  const [manualPhoneNumber, setManualPhoneNumber] = useState('');

  // Fetch phone numbers when modal opens
  useEffect(() => {
    if (show && orgId) {
      setLoadingPhoneNumbers(true);
      getPhoneNumberByOrgId(orgId)
        .then((data) => {
          const sortedNumbers = (data.phoneNumbers || []).sort((a, b) =>
            a.did.localeCompare(b.did)
          );
          setPhoneNumbers(sortedNumbers);
          if (sortedNumbers.length > 0 && !formData.phoneNumberId) {
            setFormData(prev => ({ ...prev, phoneNumberId: sortedNumbers[0].id }));
          }
        })
        .catch((e) => {
          console.error('Failed to fetch phone numbers:', e);
          toast.error('Failed to fetch phone numbers');
        })
        .finally(() => setLoadingPhoneNumbers(false));
    }
  }, [show, orgId]);

  // Reset form when modal closes
  useEffect(() => {
    if (!show) {
      setFormData({
        name: '',
        assistantId: '',
        phoneNumberId: phoneNumbers.length > 0 ? phoneNumbers[0].id : '',
        customers: [],
        scheduleType: 'now',
        scheduleDateTime: '',
      });
      setManualPhoneNumber('');
    }
  }, [show]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleCSVImport = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    const reader = new FileReader();

    reader.onload = (evt) => {
      try {
        const data = evt.target.result;
        const workbook = XLSX.read(data, { type: 'binary' });
        const sheetName = workbook.SheetNames[0];
        const sheet = workbook.Sheets[sheetName];
        const json = XLSX.utils.sheet_to_json(sheet);

        // Look for 'number' column
        const numbers = json
          .map((row) => {
            const number = row.number || row.Number || row.NUMBER || row['Phone Number'] || row['phone number'] || '';
            return number ? number.toString().trim() : '';
          })
          .filter(num => num)
          .map((number) => ({
            number: number.startsWith('+') ? number : `+91${number}`,
            numberE164CheckEnabled: true,
          }));

        if (numbers.length === 0) {
          toast.error('No phone numbers found in CSV. Please ensure the column is named "number"');
          return;
        }

        // Merge with existing customers, filtering out duplicates
        setFormData(prev => {
          const existingNumbers = prev.customers.map(c => c.number);
          const newCustomers = numbers.filter(
            customer => !existingNumbers.includes(customer.number)
          );

          const addedCount = newCustomers.length;
          const skippedCount = numbers.length - addedCount;

          // Show toast message
          if (addedCount > 0) {
            if (skippedCount > 0) {
              toast.success(`Imported ${addedCount} new phone number(s) from CSV. ${skippedCount} duplicate(s) skipped.`);
            } else {
              toast.success(`Imported ${addedCount} phone number(s) from CSV`);
            }
          } else {
            toast.warning('All phone numbers from CSV are already added');
          }

          return {
            ...prev,
            customers: [...prev.customers, ...newCustomers]
          };
        });
      } catch (error) {
        console.error('Error reading CSV:', error);
        toast.error('Error reading CSV file. Please check the format.');
      }
    };

    reader.readAsBinaryString(file);
    // Reset file input
    e.target.value = '';
  };

  const triggerFileInput = () => {
    fileInputRef.current?.click();
  };

  const downloadTemplate = () => {
    const link = document.createElement('a');
    link.href = '/assets/template/campaign-template.csv';
    link.download = 'campaign-template.csv';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const removeCustomer = (index) => {
    setFormData(prev => ({
      ...prev,
      customers: prev.customers.filter((_, i) => i !== index)
    }));
  };

  const handleAddCustomer = (e) => {
    e.preventDefault();
    if (manualPhoneNumber && manualPhoneNumber.trim()) {
      // Split by comma, newline, or space and filter empty strings
      const numbers = manualPhoneNumber
        .split(/[,\n\s]+/)
        .map(num => num.trim())
        .filter(num => num.length > 0);

      if (numbers.length === 0) {
        toast.error('Please enter at least one phone number');
        return;
      }

      // Format numbers and filter duplicates
      const formattedNumbers = numbers
        .map(number => number.startsWith('+') ? number : `+91${number}`)
        .filter((number, index, self) => self.indexOf(number) === index); // Remove duplicates in input

      // Filter out numbers that already exist in customers list
      const newNumbers = formattedNumbers.filter(
        number => !formData.customers.some(c => c.number === number)
      );

      if (newNumbers.length === 0) {
        toast.error('All phone numbers are already added');
        setManualPhoneNumber('');
        return;
      }

      // Add all new numbers
      const newCustomers = newNumbers.map(number => ({
        number,
        numberE164CheckEnabled: true,
      }));

      setFormData(prev => ({
        ...prev,
        customers: [...prev.customers, ...newCustomers]
      }));

      setManualPhoneNumber('');
      toast.success(`${newCustomers.length} phone number(s) added`);
    }
  };

  const getMinDateTime = () => {
    const now = new Date();
    now.setMinutes(now.getMinutes() + 5); // Add 5 minutes
    const year = now.getFullYear();
    const month = String(now.getMonth() + 1).padStart(2, '0');
    const day = String(now.getDate()).padStart(2, '0');
    const hours = String(now.getHours()).padStart(2, '0');
    const minutes = String(now.getMinutes()).padStart(2, '0');
    return `${year}-${month}-${day}T${hours}:${minutes}`;
  };

  const handleSubmitFormData = async (e) => {
    e.preventDefault();

    // Validate required fields
    if (!formData.name || !formData.assistantId || !formData.phoneNumberId || formData.customers.length === 0) {
      toast.error('Please fill all required fields and add at least one customer.');
      return;
    }

    // Validate schedule if "later" is selected
    if (formData.scheduleType === 'later' && !formData.scheduleDateTime) {
      toast.error('Please select a schedule date and time.');
      return;
    }

    setLoading(true);
    try {
      const payload = {
        name: formData.name,
        assistantId: formData.assistantId,
        phoneNumberId: formData.phoneNumberId,
        customers: formData.customers,
      };

      // Add schedulePlan only if scheduling later
      if (formData.scheduleType === 'later' && formData.scheduleDateTime) {
        const scheduleDate = new Date(formData.scheduleDateTime);
        payload.schedulePlan = {
          earliestAt: scheduleDate.toISOString(),
        };
      }

      const res = await createCampaign(payload);
      toast.success(res?.message || 'Campaign created successfully!');
      onSuccess?.();
      onClose();
    } catch (err) {
      console.error("Error creating campaign:", err);
      toast.error(err?.message || 'Failed to create campaign');
    } finally {
      setLoading(false);
    }
  };

  if (!show) return null;

  return (
    <motion.div
      className="modal fade show"
      tabIndex="-1"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.3, ease: 'easeInOut' }}
      style={{
        display: 'block',
        backgroundColor: 'rgba(0,0,0,0.6)',
        backdropFilter: 'blur(4px)',
        zIndex: 1050,
      }}
    >
      <div className="modal-dialog modal-dialog-centered modal-lg" style={{ maxWidth: '800px' }}>
        <div className="modal-content" style={{
          borderRadius: '16px',
          border: 'none',
          boxShadow: '0 20px 60px rgba(0,0,0,0.2)',
        }}>
          <div className="modal-header">
            <h5 className="modal-title">Create New Campaign</h5>
            <button type="button" className="btn-close" onClick={onClose}></button>
          </div>

          <form onSubmit={handleSubmitFormData}>
            <div className="modal-body" style={{ maxHeight: '70vh', overflowY: 'auto' }}>
              <div className="row g-3">
                {/* Campaign Name */}
                <div className="col-12">
                  <label className="form-label fw-semibold">Campaign Name*</label>
                  <input
                    type="text"
                    name="name"
                    className="form-control"
                    value={formData.name}
                    onChange={handleChange}
                    required
                    placeholder="e.g., Summer Sales Campaign"
                  />
                </div>

                {/* Assistant Selection */}
                <div className="col-md-6">
                  <label className="form-label fw-semibold">Assistant*</label>
                  <div className="position-relative">
                    <Icon
                      icon="lucide:bot"
                      width="18"
                      height="18"
                      style={{
                        position: 'absolute',
                        left: '12px',
                        top: '50%',
                        transform: 'translateY(-50%)',
                        pointerEvents: 'none',
                        color: '#6b7280',
                        zIndex: 1,
                      }}
                    />
                    <select
                      name="assistantId"
                      className="form-control"
                      value={formData.assistantId}
                      onChange={handleChange}
                      required
                      style={{ paddingLeft: '40px' }}
                    >
                      <option value="">Select Assistant</option>
                      {listOfAssistants.map((assistant) => (
                        <option key={assistant.id} value={assistant.id}>
                          {assistant.name}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>

                {/* Phone Number Selection */}
                <div className="col-md-6">
                  <label className="form-label fw-semibold">Phone Number*</label>
                  <div className="position-relative">
                    <Icon
                      icon="lucide:phone"
                      width="18"
                      height="18"
                      style={{
                        position: 'absolute',
                        left: '12px',
                        top: '50%',
                        transform: 'translateY(-50%)',
                        pointerEvents: 'none',
                        color: '#6b7280',
                        zIndex: 1,
                      }}
                    />
                    <select
                      name="phoneNumberId"
                      className="form-control"
                      value={formData.phoneNumberId}
                      onChange={handleChange}
                      required
                      disabled={loadingPhoneNumbers}
                      style={{ paddingLeft: '40px' }}
                    >
                      <option value="">{loadingPhoneNumbers ? 'Loading...' : 'Select Phone Number'}</option>
                      {phoneNumbers.map((phone) => (
                        <option key={phone.id} value={phone.id}>
                          {phone.did}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>

                {/* Customers Section */}
                <div className="col-12">
                  <div style={{ marginBottom: "12px" }}>
                    {/* Header row */}
                    <div
                      style={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                        marginBottom: "4px",
                      }}
                    >
                      <label
                        style={{
                          fontWeight: 600,
                          fontSize: "14px",
                          color: "#212529",
                        }}
                      >
                        Customers<span style={{ color: "red" }}> *</span>
                      </label>

                      <span
                        onClick={downloadTemplate}
                        style={{
                          cursor: "pointer",
                          fontSize: "12px",
                          color: "#0d6efd",
                          textDecoration: "underline",
                        }}
                      >
                        CSV Template
                      </span>
                    </div>

                    {/* Helper text */}
                    <div
                      style={{
                        fontSize: "12px",
                        color: "#6c757d",
                        lineHeight: "1.4",
                      }}
                    >
                      Ensure your CSV matches the template before uploading.
                    </div>
                  </div>




                  <div className="d-flex gap-2 mb-3">
                    <button
                      type="button"
                      className="btn btn-outline-primary btn-sm"
                      onClick={triggerFileInput}
                    >
                      <Icon icon="lucide:upload" width="16" height="16" className="me-1" />
                      Import CSV
                    </button>
                    <input
                      ref={fileInputRef}
                      type="file"
                      accept=".csv, .xlsx, .xls"
                      onChange={handleCSVImport}
                      style={{ display: 'none' }}
                    />
                  </div>

                  {/* Manual Add Input */}
                  <div className="mb-3">
                    <div>
                      <div className="d-flex gap-2">
                        <textarea
                          className="form-control"
                          rows="3"
                          placeholder="Enter phone numbers separated by comma, newline, or space&#10;Example: +91987xxx3210, 987654xx11, +919876xxx212&#10;Or paste multiple numbers (one per line)"
                          value={manualPhoneNumber}
                          onChange={(e) => setManualPhoneNumber(e.target.value)}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter' && (e.ctrlKey || e.metaKey)) {
                              e.preventDefault();
                              handleAddCustomer(e);
                            }
                          }}
                        />
                        <button
                          type="button"
                          className="btn btn-outline-secondary btn-sm align-self-start"
                          onClick={handleAddCustomer}
                        >
                          <Icon icon="lucide:plus" width="16" height="16" className="me-1" />
                          Add
                        </button>
                      </div>
                      <small className="text-muted">
                        You can enter multiple numbers separated by comma, newline, or space
                      </small>
                    </div>
                  </div>

                  {/* Customers Table */}
                  {formData.customers.length > 0 && (
                    <div className="border rounded">
                      <div className="table-responsive" style={{ maxHeight: '200px', overflowY: 'auto' }}>
                        <table className="table table-sm mb-0">
                          <thead className="table-light sticky-top">
                            <tr>
                              <th style={{ width: '50px' }}>S.No</th>
                              <th>Phone Number</th>
                              <th style={{ width: '100px' }}>Action</th>
                            </tr>
                          </thead>
                          <tbody>
                            {formData.customers.map((customer, index) => (
                              <tr key={index}>
                                <td>{index + 1}</td>
                                <td>{customer.number}</td>
                                <td>
                                  <button
                                    type="button"
                                    className="btn btn-sm btn-outline-danger"
                                    onClick={() => removeCustomer(index)}
                                  >
                                    <Icon icon="lucide:trash-2" width="14" height="14" />
                                  </button>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                      <div className="p-2 border-top bg-light">
                        <small className="text-muted">
                          Total: {formData.customers.length} customer(s)
                        </small>
                      </div>
                    </div>
                  )}
                </div>

                {/* Schedule Type */}
                <div className="col-12">
                  <label className="form-label fw-semibold">Schedule*</label>
                  <div className="d-flex align-items-center justify-content-between" style={{ maxWidth: '300px', margin: '0 auto' }}>
                    <div className="form-check d-flex align-items-center">
                      <input
                        className="form-check-input"
                        type="radio"
                        name="scheduleType"
                        id="scheduleNow"
                        value="now"
                        checked={formData.scheduleType === 'now'}
                        onChange={handleChange}
                        style={{ marginTop: 0 }}
                      />
                      <label className="form-check-label mb-0" htmlFor="scheduleNow">
                        Send Now
                      </label>
                    </div>
                    <div className="form-check d-flex align-items-center">
                      <input
                        className="form-check-input"
                        type="radio"
                        name="scheduleType"
                        id="scheduleLater"
                        value="later"
                        checked={formData.scheduleType === 'later'}
                        onChange={handleChange}
                        style={{ marginTop: 0 }}
                      />
                      <label className="form-check-label mb-0" htmlFor="scheduleLater">
                        Schedule Later
                      </label>
                    </div>
                  </div>
                </div>

                {/* Schedule DateTime (only if "later" is selected) */}
                {formData.scheduleType === 'later' && (
                  <div className="col-12">
                    <label className="form-label fw-semibold">Schedule Date & Time*</label>
                    <input
                      type="datetime-local"
                      name="scheduleDateTime"
                      className="form-control"
                      value={formData.scheduleDateTime}
                      onChange={handleChange}
                      min={getMinDateTime()}
                      required
                    />
                    <small className="text-muted">
                      Minimum: 5 minutes from now
                    </small>
                  </div>
                )}
              </div>
            </div>

            <div className="modal-footer d-flex justify-content-between">
              <button
                type="button"
                className="btn btn-outline-secondary"
                onClick={onClose}
                disabled={loading}
              >
                Cancel
              </button>
              <button
                type="submit"
                className="btn btn-primary"
                disabled={loading}
              >
                {loading ? 'Creating...' : 'Create Campaign'}
              </button>
            </div>
          </form>
        </div>
      </div>
    </motion.div>
  );
}

export default AddCampaignModal;

